import { useCallback, useRef, useState } from 'react';
import api from '../../services/api';

/** Clé d'idempotence générée AVANT le premier envoi (spec crédits §4.1). */
export function newRequestKey() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    // Repli (navigateurs anciens) : UUID v4 pseudo-aléatoire
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
}

const EMPTY = Object.freeze({ grading: false, verdict: null, answer: null, err: null, pendingKey: null });

/**
 * Soumission d'une réponse rédigée au correcteur, avec la discipline du mode crédits
 * (spec §4.1) : une clé d'idempotence est générée avant le premier envoi et conservée
 * pour une relance après coupure (jamais deux débits) ; une nouvelle réponse repart
 * avec une clé neuve. Partagé par la carte sujet (QrcPlayer) et l'atelier (QrcWorkshop).
 *
 * L'état est tenu PAR QUESTION (`get(qid)`) : une réponse asynchrone reste attachée à
 * la question soumise, même si l'utilisateur en ouvre une autre pendant l'attente
 * (bug reproduit par Astra le 29/09/2026 : le verdict de la Q1 s'affichait sous la Q2).
 *
 * `onCredits(credits)` : solde renvoyé par le serveur (correction ou refus 402).
 * `onGraded(q, data)` : verdict obtenu (l'appelant recharge sa liste).
 */
export default function useQrcGrading({ scope = null, force = false, onCredits = null, onGraded = null } = {}) {
    const [byQ, setByQ] = useState({});      // qid → { grading, verdict, answer, err, pendingKey }
    const keyRefs = useRef({});               // qid → clé de la soumission en cours / à relancer

    const patch = useCallback((qid, p) => setByQ(prev => ({ ...prev, [qid]: { ...(prev[qid] || EMPTY), ...p } })), []);

    /** État de correction d'une question (objet vide figé si aucune soumission). */
    const get = useCallback((qid) => byQ[qid] || EMPTY, [byQ]);

    /** Nouvelle tentative sur cette question : tout repart de zéro (verdict, erreur, clé). */
    const reset = useCallback((qid) => {
        setByQ(prev => {
            if (!prev[qid]) return prev;
            const next = { ...prev };
            delete next[qid];
            return next;
        });
        delete keyRefs.current[qid];
    }, []);

    /** La réponse est modifiée : hors relance en attente, la clé sera renouvelée à l'envoi. */
    const touch = useCallback((qid) => {
        if (!byQ[qid]?.pendingKey) delete keyRefs.current[qid];
    }, [byQ]);

    const submit = useCallback(async (q, answer, retryKey = null) => {
        const qid = q.id;
        if (!answer.trim()) return;
        // Une nouvelle réponse = une nouvelle clé ; une relance garde la sienne.
        const requestKey = retryKey || keyRefs.current[qid] || newRequestKey();
        keyRefs.current[qid] = requestKey;
        patch(qid, { grading: true, err: null });
        try {
            const { data } = await api.post('/qcm/qrc-grade.php', {
                qrc_question_id: qid, answer, origin: 'sous-sujet',
                scope_slug: scope || undefined,
                request_key: requestKey,
                ...(force ? { policy: 'credits' } : {}),
            }, { timeout: 240000 });
            const d = data.data || {};
            if (d.replay === 'pending') {
                // Correction en cours côté serveur : on garde la clé, on propose de réinterroger.
                patch(qid, { grading: false, pendingKey: requestKey, err: 'Correction en cours — réessaie dans quelques secondes, ta réponse ne sera pas facturée deux fois.' });
            } else if (d.replay === 'deleted') {
                delete keyRefs.current[qid];
                patch(qid, { grading: false, pendingKey: null, err: data.message || 'Le résultat de cette correction a été effacé.' });
            } else {
                delete keyRefs.current[qid];
                // La copie envoyée reste consultable à côté du verdict (« Ta réponse »).
                patch(qid, { grading: false, verdict: d.verdict, answer, pendingKey: null, err: null });
                if (d.credits && onCredits) onCredits(d.credits);
                if (onGraded) onGraded(q, d);
            }
        } catch (e) {
            const status = e.response?.status;
            const msg = e.response?.data?.message || 'Correction indisponible';
            let pendingKey = null;
            if (status === 409 && e.response?.data?.errors?.expired) {
                // Soumission expirée : la prochaine tentative repart avec une clé neuve.
                delete keyRefs.current[qid];
            } else if (status === 402) {
                const c = e.response?.data?.errors?.credits;
                if (c && onCredits) onCredits(c);
                delete keyRefs.current[qid];
            } else {
                // Erreur réseau / 5xx : on garde la clé pour relancer sans double débit.
                pendingKey = requestKey;
            }
            patch(qid, { grading: false, pendingKey, err: (status ? `${status} — ` : '') + msg });
        }
    }, [scope, force, onCredits, onGraded, patch]);

    return { get, submit, reset, touch };
}
